import React from "react";
import { Link } from "react-router-dom";
import { Card } from "../ui/card";
import { Button } from "../ui/button";

/**
 * Full-page pending-verification message for orgs and individuals.
 *
 * @param {Object} props
 * @param {"organization"|"individual"} props.variant
 * @returns {JSX.Element}
 */
export default function AccountUnderReview({ variant = "organization" }) {
  const isOrg = variant === "organization";
  return (
    <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-ui-background p-4">
      <Card className="w-full max-w-2xl overflow-hidden border-ui-border shadow-profile-md">
        <div className="flex flex-col justify-center px-6 py-8 md:px-10 md:py-12">
          <h2 className="mb-4 text-2xl font-extrabold text-text-primary md:text-3xl">
            {isOrg ? "Organization under review" : "Account under review"}
          </h2>
          <div className="mb-6 space-y-3 text-left text-base leading-relaxed text-text-secondary md:text-lg">
            <p>
              <span className="mb-1 block font-semibold text-text-primary">
                Thank you for joining Assembly.
              </span>
            </p>
            <p>
              Assembly helps ministries and leaders connect, collaborate, and support the work God is
              doing.
            </p>
            <p>
              Your account is under review by our team. Once approved, you can
              {isOrg
                ? " invite staff and connect with other leaders"
                : " connect with other leaders on the platform"}
              . This usually takes about three business days.
            </p>
            <p>We appreciate your patience. Questions? Reach out via support below.</p>
          </div>
          <div className="mb-6 rounded-lg border border-ui-border bg-ui-muted p-4 md:p-5">
            <h3 className="mb-3 text-lg font-semibold text-text-primary md:text-xl">
              {isOrg ? "Guidelines for organizations" : "Guidelines for members"}
            </h3>
            <ol className="list-decimal space-y-2 pl-5 text-left text-sm leading-relaxed text-text-secondary md:text-base">
              <li>
                <span className="font-semibold text-text-primary">Authenticity:</span>{" "}
                {isOrg
                  ? "Organizations must represent real groups, churches, ministries, or causes aligned with Christian values."
                  : "Accounts must represent real ministry leaders aligned with Christian values."}
              </li>
              <li>
                <span className="font-semibold text-text-primary">Respect:</span> Treat all members
                and organizations with respect and dignity. Healthy, faith-filled discussion is
                welcome, but malicious or argumentative behavior is not.
              </li>
              <li>
                <span className="font-semibold text-text-primary">Privacy:</span> Do not share private
                information of others without explicit permission.
              </li>
              <li>
                <span className="font-semibold text-text-primary">Collaboration:</span> Use this
                platform to encourage and unite members, not to sow discord or division within the
                Body.
              </li>
            </ol>
          </div>
          <div className="mb-2 flex items-center gap-3">
            <div className="h-px flex-1 bg-ui-border" />
            <span className="text-sm text-text-muted" aria-hidden>
              ·
            </span>
            <div className="h-px flex-1 bg-ui-border" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to="/feedback">Contact support</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/explore">Explore Assembly</Link>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
